import React from "react"
import { num } from "./format"

// Distinct slice palette (hex — SVG strokes can't use Tailwind bg-* classes).
const SLICE_COLORS = [
  "#f43f5e", // rose-500
  "#f97316", // orange-500
  "#eab308", // yellow-500
  "#14b8a6", // teal-500
  "#6366f1", // indigo-500
  "#ec4899", // pink-500
  "#8b5cf6", // violet-500
  "#64748b", // slate-500
]

const RADIUS = 56
const STROKE = 28
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

// Donut chart of uninstalled merchants by reason. Slice fractions are share of
// the shown reasons (so the ring closes); the table carries the exact counts and
// each reason's % of ALL uninstalls. Reasons are localized by Shopify per
// merchant locale, so variants render as separate slices.
function Donut({ rows, total }) {
  let offset = 0
  return (
    <svg viewBox="0 0 140 140" className="h-48 w-48 max-w-full sm:h-72 sm:w-72 -rotate-90" role="img" aria-label="Uninstall reasons donut chart">
      <circle cx="70" cy="70" r={RADIUS} fill="none" stroke="currentColor" strokeWidth={STROKE} className="text-gray-100 dark:text-gray-800" />
      {rows.map((row, i) => {
        const frac = total > 0 ? (row.count || 0) / total : 0
        const dash = frac * CIRCUMFERENCE
        const seg = (
          <circle
            key={row.reason}
            cx="70"
            cy="70"
            r={RADIUS}
            fill="none"
            stroke={SLICE_COLORS[i % SLICE_COLORS.length]}
            strokeWidth={STROKE}
            strokeDasharray={`${dash} ${CIRCUMFERENCE - dash}`}
            strokeDashoffset={-offset}
          />
        )
        offset += dash
        return seg
      })}
    </svg>
  )
}

export function UninstallReasons({ data, copy, emptyLabel }) {
  const rows = data?.rows || []
  if (rows.length === 0) return <p className="text-sm text-gray-500 dark:text-gray-400">{emptyLabel}</p>

  const shownTotal = rows.reduce((s, r) => s + (r.count || 0), 0)

  return (
    <div className="grid grid-cols-1 items-center gap-6 sm:grid-cols-2">
      {/* Donut */}
      <div className="flex flex-col items-center justify-center">
        <Donut rows={rows} total={shownTotal} />
        <p className="mt-3 text-center text-xs text-gray-400 dark:text-gray-500">
          {copy.coverage.replace("{with}", num(data.with_reason)).replace("{total}", num(data.total_uninstalled))}
        </p>
      </div>

      {/* Table */}
      <div className="w-full overflow-x-auto">
      <table className="w-full min-w-[280px] text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">
            <th className="pb-2 font-medium">{copy.reason}</th>
            <th className="pb-2 text-right font-medium">{copy.count}</th>
            <th className="pb-2 text-right font-medium">{copy.share}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {rows.map((row, i) => (
            <tr key={row.reason}>
              <td className="py-2 text-gray-700 dark:text-gray-300">
                <span className="flex items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 flex-none rounded-full"
                    style={{ backgroundColor: SLICE_COLORS[i % SLICE_COLORS.length] }}
                  />
                  <span className="truncate" title={row.reason}>{row.reason}</span>
                </span>
              </td>
              <td className="py-2 text-right tabular-nums text-gray-900 dark:text-gray-100">{num(row.count)}</td>
              <td className="py-2 text-right tabular-nums text-gray-500 dark:text-gray-400">{row.pct}%</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  )
}
